import client from '@/apollo-client';
import aiMagicWand from '@/assets/icons/ai-suggested-icon.png';
import { FirmInfoType } from '@/components/settings-page/platformSettings/firmInfo';
import { ITemplateStylingSettings } from '@/components/settings-page/platformSettings/templatesSettings/types';
import { GET_ASSET_SIGNED_URL_QUERY } from '@/components/settings-page/queries';
import { IFieldEditor, IFieldEditorAttachment } from '@/shared/types/fieldEditor';
import { getBaseUrl } from '@/shared/utils/url';
import * as docx from 'docx';
import fileDownload from 'js-file-download';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import { Root } from 'remark-parse/lib';
import { DefaultTheme } from 'styled-components/dist/types';
import { unified } from 'unified';
import { parseMarkdownHtmlImages } from '../utils';
import { getDocxFooter } from './components/footer';
import { getDocxHeader } from './components/header';

export const transparentDocxTableBorders = {
  top: { style: docx.BorderStyle.SINGLE, size: 0, color: 'FFFFFF' },
  bottom: { style: docx.BorderStyle.SINGLE, size: 0, color: 'FFFFFF' },
  left: { style: docx.BorderStyle.SINGLE, size: 0, color: 'FFFFFF' },
  right: { style: docx.BorderStyle.SINGLE, size: 0, color: 'FFFFFF' }
};

export const getScorePropertiesForDOCX = (score: number, theme: DefaultTheme) => {
  if (score > 0) {
    return {
      color: theme.context.light,
      fill: theme.context.success,
      text: `+${score}`
    };
  } else if (score === 0) {
    return {
      color: '#474747',
      fill: '#F4F5F6',
      text: `${score}`
    };
  } else {
    return {
      color: theme.context.light,
      fill: theme.context.error,
      text: `${score}`
    };
  }
};

export const createInvestmentParagraphsForDOCX = ({
  investmentName,
  templateName,
  investmentDescription,
  headerFontColor,
  theme,
  score,
  displayScore
}: {
  investmentName: string;
  templateName: string;
  investmentDescription: string;
  headerFontColor: string;
  theme: DefaultTheme;
  score?: number;
  displayScore?: boolean;
}) => {
  const investmentNameParagraph = new docx.Paragraph({
    heading: docx.HeadingLevel.HEADING_1,
    spacing: { after: !!templateName ? 200 : 300 },
    children: [
      new docx.TextRun({
        text: investmentName,
        bold: true,
        color: headerFontColor,
        size: '18pt'
      })
    ]
  });

  const scoreParagraph = new docx.Paragraph({
    children: [
      new docx.TextRun({
        text: displayScore ? `Total score ${getScorePropertiesForDOCX(score, theme).text}` : '',
        size: 30,
        color: getScorePropertiesForDOCX(score, theme).color,
        bold: true
      })
    ],
    alignment: docx.AlignmentType.CENTER
  });

  const table = new docx.Table({
    rows: [
      new docx.TableRow({
        children: [
          new docx.TableCell({
            children: [investmentNameParagraph],
            width: {
              size: 7500,
              type: docx.WidthType.DXA
            },
            borders: transparentDocxTableBorders
          }),
          new docx.TableCell({
            children: [scoreParagraph],
            width: {
              size: 2500,
              type: docx.WidthType.DXA
            },
            shading: { fill: displayScore ? getScorePropertiesForDOCX(score, theme).fill : '#FFFFFF' },
            verticalAlign: docx.VerticalAlign.CENTER,
            borders: transparentDocxTableBorders
          })
        ]
      })
    ]
  });

  const templateNameParagraph = new docx.Paragraph({
    spacing: { after: 300 },
    children: [
      new docx.TextRun({
        text: templateName,
        color: theme.font.base
      })
    ]
  });

  const investmentDescriptionParagraph = new docx.Paragraph({
    spacing: { before: 300, after: 300 },
    children: [
      new docx.TextRun({
        text: investmentDescription,
        color: theme.font.base
      })
    ]
  });

  const docxData = [];

  docxData.push(table);

  if (templateName) {
    docxData.push(templateNameParagraph);
  }

  if (investmentDescription) {
    docxData.push(investmentDescriptionParagraph);
  }

  return docxData;
};

export const getDocxImage = async (url: string, assetKey?: string, withUpdatingUrl?: boolean): Promise<Uint8Array | null> => {
  if (withUpdatingUrl) {
    try {
      const result = await client.query({
        query: GET_ASSET_SIGNED_URL_QUERY,
        fetchPolicy: 'network-only',
        variables: {
          assetKey
        }
      });

      const response = await fetch(result.data.getAssetSignedUrl);
      if (!response.ok) {
        return null;
      }

      const arrayBuffer = await response.arrayBuffer();
      return new Uint8Array(arrayBuffer);
    } catch (error) {
      console.log(error);
    }
  }

  try {
    const response = await fetch(url);
    if (!response.ok) {
      return null;
    }

    const arrayBuffer = await response.arrayBuffer();
    return new Uint8Array(arrayBuffer);
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const getAiSuggestedSectionDOCX = async (fields: IFieldEditor[], theme: DefaultTheme) => {
  const aiSuggestedSectionData = [];

  const aiSuggestedIcon = await getDocxImage(aiMagicWand, '');

  const imageRun = new docx.ImageRun({
    type: 'png',
    data: aiSuggestedIcon ?? '',
    transformation: {
      width: 15,
      height: 15
    }
  });

  const textRun = new docx.TextRun({
    text: fields.length > 1 ? '* These fields are AI Suggested' : 'This field is AI Suggested',
    color: theme.font.weak
  });

  const table = new docx.Table({
    rows: [
      new docx.TableRow({
        children: [
          new docx.TableCell({
            children: [
              new docx.Paragraph({
                children: [imageRun]
              })
            ],
            borders: {
              top: { style: docx.BorderStyle.SINGLE, size: 0, color: 'FFFFFF' },
              bottom: { style: docx.BorderStyle.SINGLE, size: 0, color: 'FFFFFF' },
              left: { style: docx.BorderStyle.SINGLE, size: 0, color: 'FFFFFF' },
              right: { style: docx.BorderStyle.SINGLE, size: 6, color: 'C7CBD2' }
            },
            margins: {
              top: 100,
              bottom: 100,
              right: 100
            }
          }),
          new docx.TableCell({
            children: [
              new docx.Paragraph({
                children: [textRun]
              })
            ],
            borders: transparentDocxTableBorders,
            margins: {
              top: 100,
              bottom: 100,
              left: 100
            }
          })
        ]
      })
    ]
  });

  aiSuggestedSectionData.push(new docx.Paragraph({}));
  aiSuggestedSectionData.push(table);
  aiSuggestedSectionData.push(new docx.Paragraph({}));

  return aiSuggestedSectionData;
};

// Functions to render markdown content to DOCX format
const extractText = (node: any): string => {
  if (node.type === 'text') return node.value;
  if (Array.isArray(node.children)) {
    return node.children
      .filter((child: any) => child.type !== 'list')
      .map(extractText)
      .join('');
  }
  return '';
};

const isInline = (node: any) => {
  return node instanceof docx.TextRun || node instanceof docx.ImageRun;
};

const renderNodeForDocx = async ({
  node,
  attachments,
  theme,
  headerFontColor,
  bodyFontColor,
  accentColor,
  listLevel = 0,
  maxImageWidth = 665
}: {
  node: any;
  attachments: IFieldEditorAttachment[];
  theme: DefaultTheme;
  headerFontColor: string;
  bodyFontColor: string;
  accentColor: string;
  listLevel: number;
  maxImageWidth: number;
}): Promise<any[] | any> => {
  switch (node.type) {
    case 'root': {
      const children = await Promise.all(
        node.children.map((child: any) =>
          renderNodeForDocx({ node: child, attachments, theme, headerFontColor, bodyFontColor, accentColor, listLevel, maxImageWidth })
        )
      );
      return children.flat().filter(Boolean);
    }

    case 'paragraph': {
      if (!node.children || !Array.isArray(node.children) || node.children.length === 0) {
        return null;
      }

      const childNodes = await Promise.all(
        node.children.map(async (child: any) => {
          const result = await renderNodeForDocx({
            node: child,
            attachments,
            theme,
            headerFontColor,
            bodyFontColor,
            accentColor,
            listLevel,
            maxImageWidth
          });
          return result ? result : null;
        })
      );

      const validChildren = childNodes.flat().filter(Boolean);

      if (validChildren.length === 0) {
        return null;
      }

      const inlineChildren = validChildren.filter(isInline);
      const blockChildren = validChildren.filter((child) => !isInline(child));

      const result: any[] = [];

      if (inlineChildren.length > 0) {
        result.push(new docx.Paragraph({ children: inlineChildren }));
      }

      if (blockChildren.length > 0) {
        result.push(...blockChildren);
      }

      if (result.length === 0) {
        return null;
      }

      if (result.length === 1) {
        return result[0];
      }

      return result;
    }

    case 'text': {
      return new docx.TextRun({ text: node.value, color: bodyFontColor });
    }

    case 'strong': {
      const children = await Promise.all(
        node.children.map((child: any) =>
          renderNodeForDocx({ node: child, attachments, theme, headerFontColor, bodyFontColor, accentColor, listLevel, maxImageWidth })
        )
      );

      return new docx.TextRun({
        children: children.flat().filter(Boolean),
        color: bodyFontColor,
        bold: true
      });
    }

    case 'emphasis': {
      const children = await Promise.all(
        node.children.map((child: any) =>
          renderNodeForDocx({ node: child, attachments, theme, headerFontColor, bodyFontColor, accentColor, listLevel, maxImageWidth })
        )
      );

      return new docx.TextRun({
        children: children.flat().filter(Boolean),
        color: bodyFontColor,
        italics: true
      });
    }

    case 'heading': {
      const children = await Promise.all(
        node.children.map((child: any) => {
          if (child.type === 'text') {
            return new docx.TextRun({ text: child.value, color: headerFontColor });
          }
          return renderNodeForDocx({ node: child, attachments, theme, headerFontColor, bodyFontColor, accentColor, listLevel, maxImageWidth });
        })
      );

      return new docx.Paragraph({
        children: children.flat().filter(Boolean),
        heading:
          node.depth >= 1 && node.depth <= 6
            ? docx.HeadingLevel[`HEADING_${node.depth}` as keyof typeof docx.HeadingLevel]
            : docx.HeadingLevel.HEADING_1,
        spacing: { before: 300, after: 200 },
        border: {
          bottom: {
            color: theme.layer[2],
            space: 2,
            size: 2,
            style: 'single'
          }
        }
      });
    }

    case 'list': {
      const items = await Promise.all(
        node.children.map((item: any, index: number) =>
          renderNodeForDocx({
            node: { ...item, index, ordered: node.ordered },
            attachments,
            theme,
            headerFontColor,
            bodyFontColor,
            accentColor,
            listLevel,
            maxImageWidth
          })
        )
      );

      return items.flat().filter(Boolean);
    }

    case 'listItem': {
      const children = await Promise.all(
        node.children.map((child: any) => {
          if (child.type === 'list') {
            return renderNodeForDocx({
              node: child,
              attachments,
              theme,
              headerFontColor,
              bodyFontColor,
              accentColor,
              listLevel: listLevel + 1,
              maxImageWidth
            });
          }
          return renderNodeForDocx({ node: child, attachments, theme, headerFontColor, bodyFontColor, accentColor, listLevel, maxImageWidth });
        })
      );

      const listPrefix = node.ordered ? `${node.index + 1}. ` : '• ';

      const currentParagraph = new docx.Paragraph({
        children: [
          new docx.TextRun({
            text: listPrefix,
            color: bodyFontColor,
            bold: node.ordered
          }),
          new docx.TextRun({
            text: extractText(node),
            color: bodyFontColor
          })
        ],
        indent: {
          left: listLevel === 0 ? docx.convertInchesToTwip(0.2) : docx.convertInchesToTwip(0.2 + listLevel * 0.5)
        }
      });

      const nestedLists = children
        .map((child, idx) => (node.children[idx]?.type === 'list' ? child : null))
        .filter(Boolean)
        .flat();

      return [currentParagraph, ...nestedLists];
    }

    case 'table': {
      const numberOfColumns = node.children[0]?.children.length || 1;
      const cellWidth = Math.floor(100 / numberOfColumns);

      const rows = await Promise.all(
        node.children.map(async (row: any, index: number) => {
          const cells = await Promise.all(
            row.children.map(async (cell: any) => {
              const children = await Promise.all(
                cell.children.map((child: any) =>
                  renderNodeForDocx({
                    node: child,
                    attachments,
                    theme,
                    headerFontColor,
                    bodyFontColor: index === 0 ? theme.context.light : bodyFontColor,
                    accentColor,
                    listLevel,
                    maxImageWidth
                  })
                )
              );

              return new docx.TableCell({
                children: [
                  new docx.Paragraph({
                    children: children.flat().filter(Boolean)
                  })
                ],
                margins: { top: 100, bottom: 100, left: 100, right: 100 },
                width: { size: cellWidth, type: docx.WidthType.PERCENTAGE },
                borders: {
                  top: { style: docx.BorderStyle.SINGLE, size: 1, color: theme.border.base },
                  bottom: { style: docx.BorderStyle.SINGLE, size: 1, color: theme.border.base },
                  left: { style: docx.BorderStyle.SINGLE, size: 1, color: theme.border.base },
                  right: { style: docx.BorderStyle.SINGLE, size: 1, color: theme.border.base }
                },
                ...(index === 0
                  ? {
                      shading: {
                        fill: accentColor
                      }
                    }
                  : {})
              });
            })
          );

          return new docx.TableRow({ children: cells });
        })
      );

      const table = new docx.Table({
        rows,
        width: { size: 100, type: docx.WidthType.PERCENTAGE }
      });

      return [new docx.Paragraph({}), table];
    }

    case 'html': {
      const images = parseMarkdownHtmlImages(node.value);

      if (images.length === 0) {
        return null;
      }

      for await (const img of images) {
        const currentAttachment = attachments.find((attachment) => getBaseUrl(attachment.url) === getBaseUrl(img.src));

        if (!currentAttachment) continue;

        const imageData = await getDocxImage(currentAttachment.url, currentAttachment.assetKey);

        let dimensions = { width: img.width || maxImageWidth, height: img.height };
        if (dimensions.width > maxImageWidth) {
          const aspectRatio = dimensions.height / dimensions.width;
          dimensions = { width: maxImageWidth, height: Math.round(maxImageWidth * aspectRatio) };
        }

        return new docx.Paragraph({
          children: [
            new docx.ImageRun({
              type: 'png',
              data: imageData,
              transformation: dimensions
            })
          ],
          spacing: { before: 200, after: 200 }
        });
      }

      return null;
    }

    default: {
      return null;
    }
  }
};

export const convertMarkdownToDocx = async ({
  markdown,
  attachments,
  theme,
  headerFontColor,
  accentColor,
  maxImageWidth = 665
}: {
  markdown: string;
  attachments: IFieldEditorAttachment[];
  theme: DefaultTheme;
  headerFontColor: string;
  accentColor: string;
  maxImageWidth?: number;
}) => {
  const tree = unified().use(remarkParse).use(remarkGfm).parse(markdown) as Root;

  const rendered = await renderNodeForDocx({
    node: tree,
    attachments,
    theme,
    headerFontColor,
    bodyFontColor: theme.font.base,
    accentColor,
    listLevel: 0,
    maxImageWidth
  });
  return Array.isArray(rendered) ? rendered : [rendered];
};

export const generateDocxTemplate = async (
  formattedData: docx.FileChild[],
  firmInfoData: FirmInfoType,
  templateStylingData: ITemplateStylingSettings,
  fileName: string,
  theme: DefaultTheme
) => {
  const doc = new docx.Document({
    // numbering: {
    //   config: [
    //     {
    //       reference: 'decimal-level-0',
    //       levels: [
    //         {
    //           level: 0,
    //           format: docx.LevelFormat.DECIMAL,
    //           text: '%1.',
    //           alignment: docx.AlignmentType.START,
    //           style: {
    //             paragraph: {
    //               indent: { left: docx.convertInchesToTwip(0.5), hanging: docx.convertInchesToTwip(0.25) }
    //             }
    //           }
    //         }
    //       ]
    //     },
    //     {
    //       reference: 'decimal-level-1',
    //       levels: [
    //         {
    //           level: 0,
    //           format: docx.LevelFormat.DECIMAL,
    //           text: '%1.',
    //           alignment: docx.AlignmentType.START,
    //           style: {
    //             paragraph: {
    //               indent: { left: docx.convertInchesToTwip(1.2), hanging: docx.convertInchesToTwip(0.25) }
    //             }
    //           }
    //         }
    //       ]
    //     },
    //     {
    //       reference: 'decimal-level-2',
    //       levels: [
    //         {
    //           level: 0,
    //           format: docx.LevelFormat.DECIMAL,
    //           text: '%1.',
    //           alignment: docx.AlignmentType.START,
    //           style: {
    //             paragraph: {
    //               indent: { left: docx.convertInchesToTwip(2), hanging: docx.convertInchesToTwip(0.25) }
    //             }
    //           }
    //         }
    //       ]
    //     },
    //     {
    //       reference: 'decimal-level-3',
    //       levels: [
    //         {
    //           level: 0,
    //           format: docx.LevelFormat.DECIMAL,
    //           text: '%1.',
    //           alignment: docx.AlignmentType.START,
    //           style: {
    //             paragraph: {
    //               indent: { left: docx.convertInchesToTwip(3), hanging: docx.convertInchesToTwip(0.25) }
    //             }
    //           }
    //         }
    //       ]
    //     }
    //   ]
    // },
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: 1600,
              right: 800,
              left: 800,
              header: 0
            }
          }
        },
        children: formattedData,
        headers: {
          default: await getDocxHeader(templateStylingData, theme)
        },
        footers: {
          default: getDocxFooter(firmInfoData, theme)
        }
      }
    ]
  });

  docx.Packer.toBlob(doc).then((blob) => {
    fileDownload(blob, fileName);
  });
};
